import * as React from 'react'
import Utils from '../../../utilities/Utils'
import TimeHelper from '../../../helpers/TimeHelper'
import { InvoicesController } from '../../../controllers/accountant'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group'

interface IProps {
  workspaceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ExportInvoicesModal = (props: IProps) => {
  const { workspaceId, open, onOpenChange } = props

  const [selectedType, setSelectedType] = React.useState<'pdf' | 'ubl'>('pdf')
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear())
  const [selectedQuarter, setSelectedQuarter] = React.useState(Utils.getCurrentQuarter())
  const [selectedBookingType, setSelectedBookingType] = React.useState<'all' | 'booked' | 'unbooked'>('all')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const typeOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'UBL', value: 'ubl' },
  ]
  const yearOptions = TimeHelper.getYearRange((new Date()).getFullYear() - 2)
  const yearItems = yearOptions.map(year => ({ label: `${year}`, value: `${year}` }))
  const bookingOptions = [
    { label: 'Alle', value: 'all' },
    { label: 'Niet ingeboekt', value: 'unbooked' },
    { label: 'Ingeboekt', value: 'booked' },
  ]

  const submitExport = () => {
    setIsSubmitting(true)

    InvoicesController
      .requestExport(workspaceId, selectedType, selectedYear, selectedQuarter, selectedBookingType)
      .then(response => {
        if (response.success) {
          toast.success('U ontvangt het archief met facturen voor de geselecteerde periode via email.')
          onOpenChange(false)
        }
      })
      .catch(console.error)
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitExport()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exporteer facturen</DialogTitle>
        </DialogHeader>

        <form onSubmit={onFormSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Type</Label>
            <Select items={typeOptions} value={selectedType} onValueChange={(value: 'pdf' | 'ubl') => setSelectedType(value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="ubl">UBL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Jaar</Label>
            <Select items={yearItems} value={`${selectedYear}`} onValueChange={(value: string) => setSelectedYear(Number(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={`${year}`}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Boekhouding status</Label>
            <Select items={bookingOptions} value={selectedBookingType} onValueChange={(value: 'all' | 'booked' | 'unbooked') => setSelectedBookingType(value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bookingOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Kwartaal</Label>
            <ToggleGroup
              variant="outline"
              spacing={0}
              value={[`${selectedQuarter}`]}
              onValueChange={(value) => value[0] && setSelectedQuarter(Number(value[0]))}
            >
              {[1, 2, 3, 4].map(quarter => (
                <ToggleGroupItem key={quarter} value={`${quarter}`} className="flex-1">
                  {`Q${quarter}`}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </form>

        <DialogFooter>
          <Button onClick={submitExport} disabled={isSubmitting}>
            {'Exporteer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExportInvoicesModal
